import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { getImpact } from "@/lib/api";

function Counter({ value, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView || !value) return;
    let start = 0;
    const duration = 1400;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.floor(eased * value));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  return (
    <span ref={ref} className="font-mono">
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

const fmt = (n) => {
  if (n >= 1_000_000) return { value: +(n / 1_000_000).toFixed(2), suffix: "M" };
  if (n >= 1_000) return { value: +(n / 1_000).toFixed(1), suffix: "K" };
  return { value: n, suffix: "" };
};

export default function ImpactStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getImpact().then(setStats).catch((e) => console.error("Failed to load impact stats:", e));
  }, []);

  const cards = [
    { label: "Minimum Funding Goal", value: stats?.funding_goal || 250000, prefix: "$", suffix: "+", testid: "stat-funding-goal" },
    { label: "Trees We Aim to Plant", value: stats?.trees_goal || 200000, suffix: "+", testid: "stat-trees-goal" },
    { label: "Hectares We Aim to Restore", value: stats?.hectares_goal || 80, suffix: "+", testid: "stat-hectares-goal" },
    { label: "Communities We Aim to Support", value: stats?.communities_goal || 25, suffix: "+", testid: "stat-communities-goal" },
  ];

  return (
    <section className="py-20 border-y border-white/10 bg-card/40" data-testid="impact-stats">
      <div className="px-6 md:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">Our Minimum Mission Targets</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold tracking-tight">
            What every $AFTBRN token is built to fund.
          </h2>
          <p className="mt-4 text-sm text-muted-foreground max-w-2xl mx-auto">
            These are our <span className="text-foreground font-semibold">minimum</span> presale goals — not funds raised.
            Every dollar contributed will be tracked on-chain and fully verifiable on BaseScan once the presale goes live.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {cards.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-background p-8 text-center"
              data-testid={c.testid}
            >
              <div className="text-3xl sm:text-5xl font-display font-bold text-ember">
                <Counter value={c.value} prefix={c.prefix || ""} suffix={c.suffix || ""} />
              </div>
              <div className="mt-3 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {c.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
